import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { LifeBuoy, Plus, Send } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/common";
import Modal, { TextField, TextArea, SelectField } from "@/components/Modal";
import { toast } from "sonner";

export const TICKET_STATUS = {
  ouvert: { label: "Ouvert", color: "bg-blue-100 text-blue-700" },
  en_cours: { label: "En cours", color: "bg-amber-100 text-amber-700" },
  resolu: { label: "Résolu", color: "bg-green-100 text-green-700" },
  ferme: { label: "Fermé", color: "bg-slate-200 text-slate-500" },
};

export default function ClientSupport() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [active, setActive] = useState(null);
  const load = useCallback(() => api.get("/support/tickets").then((r) => setTickets(r.data)).catch(() => setTickets([])), []);
  useEffect(() => { if (user) load(); }, [user, load]);

  if (!user) {
    return (
      <div className="max-w-xl mx-auto text-center py-12">
        <LifeBuoy className="w-10 h-10 mx-auto text-[#C85A32] mb-3" />
        <h1 className="font-display text-2xl font-bold">Support TOGOBATI</h1>
        <p className="text-slate-500 mt-2">Connectez-vous pour signaler un problème et suivre vos demandes.</p>
        <Link to="/login" className="inline-block mt-4 bg-[#C85A32] text-white font-bold px-5 py-2.5 rounded-lg" data-testid="support-login-cta">Se connecter</Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-display text-2xl font-bold flex items-center gap-2"><LifeBuoy className="w-6 h-6 text-[#C85A32]" /> Support</h1>
        <button onClick={() => setShowNew(true)} className="bg-[#C85A32] text-white font-bold text-sm px-4 py-2 rounded-lg inline-flex items-center gap-1" data-testid="new-ticket-btn"><Plus className="w-4 h-4" /> Nouveau ticket</button>
      </div>
      {!tickets ? <Loader /> : tickets.length === 0 ? (
        <p className="text-slate-400 text-sm text-center py-8">Aucune demande pour le moment. Ouvrez un ticket si vous rencontrez un problème.</p>
      ) : (
        <div className="space-y-2">
          {tickets.map((t) => (
            <button key={t.id} onClick={() => setActive(t.id)} className="w-full text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-[#C85A32] transition" data-testid={`ticket-${t.id}`}>
              <div className="flex items-center gap-2">
                <span className="font-num text-xs text-slate-400">{t.ticket_number}</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${TICKET_STATUS[t.status]?.color}`}>{TICKET_STATUS[t.status]?.label}</span>
                <span className="ml-auto text-xs text-slate-400">{formatDate(t.updated_at)}</span>
              </div>
              <p className="font-semibold mt-1">{t.subject}</p>
              <p className="text-sm text-slate-500 line-clamp-1">{t.message}</p>
            </button>
          ))}
        </div>
      )}
      {showNew && <NewTicket onClose={() => setShowNew(false)} onDone={load} />}
      {active && <TicketThread id={active} onClose={() => setActive(null)} onChanged={load} />}
    </div>
  );
}

function NewTicket({ onClose, onDone }) {
  const [f, setF] = useState({ subject: "", message: "", category: "general" });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/support/tickets", f); toast.success("Ticket envoyé"); onDone(); onClose(); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Modal title="Nouveau ticket" onClose={onClose} testid="ticket-form">
      <form onSubmit={submit} className="space-y-3">
        <TextField label="Sujet" value={f.subject} onChange={set("subject")} required testid="ticket-subject" />
        <SelectField label="Catégorie" value={f.category} onChange={set("category")} testid="ticket-category" placeholder={null}
          options={[{ value: "general", label: "Général" }, { value: "commande", label: "Commande" }, { value: "livraison", label: "Livraison" }, { value: "installation", label: "Installation" }, { value: "paiement", label: "Paiement" }]} />
        <TextArea label="Décrivez votre problème" value={f.message} onChange={set("message")} required testid="ticket-message" rows={4} />
        <button className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg" data-testid="ticket-submit">Envoyer</button>
      </form>
    </Modal>
  );
}

export function TicketThread({ id, onClose, onChanged, staff }) {
  const [t, setT] = useState(null);
  const [msg, setMsg] = useState("");
  const base = staff ? "/support/tickets" : "/support/tickets";
  const load = useCallback(() => api.get(`/support/tickets/${id}`).then((r) => setT(r.data)), [id]);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!msg.trim()) return;
    try { await api.post(`${base}/${id}/reply`, { message: msg }); setMsg(""); load(); onChanged && onChanged(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const setStatus = async (status) => {
    try { await api.put(`${base}/${id}/status`, { status }); toast.success("Statut mis à jour"); load(); onChanged && onChanged(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Modal title={t ? `${t.ticket_number} — ${t.subject}` : "Ticket"} onClose={onClose} testid="ticket-thread" size="lg">
      {!t ? <Loader /> : (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${TICKET_STATUS[t.status]?.color}`} data-testid="thread-status">{TICKET_STATUS[t.status]?.label}</span>
            {staff && (
              <select value={t.status} onChange={(e) => setStatus(e.target.value)} className="ml-auto text-xs border border-slate-300 rounded-lg px-2 py-1" data-testid="thread-status-select">
                {Object.entries(TICKET_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            )}
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs text-slate-400 mb-1">{t.user_name} · {formatDate(t.created_at)}</p>
            <p className="text-sm">{t.message}</p>
          </div>
          {(t.replies || []).map((r, i) => (
            <div key={i} className={`rounded-lg p-3 ${r.is_staff ? "bg-[#FDF3EE] ml-6" : "bg-slate-50 mr-6"}`} data-testid={`reply-${i}`}>
              <p className="text-xs text-slate-400 mb-1">{r.author}{r.is_staff ? " · Support" : ""} · {formatDate(r.at)}</p>
              <p className="text-sm">{r.message}</p>
            </div>
          ))}
          {["resolu", "ferme"].includes(t.status) && !staff ? (
            <p className="text-xs text-center text-slate-400">Ce ticket est {TICKET_STATUS[t.status].label.toLowerCase()}.</p>
          ) : (
            <div className="flex gap-2">
              <input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Votre message…" className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="reply-input" />
              <button onClick={send} className="bg-[#C85A32] text-white px-4 rounded-lg inline-flex items-center gap-1" data-testid="reply-send"><Send className="w-4 h-4" /></button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
